// The Stats Table on ?live=1 (Ben, 2026-11-02: Catalyst's Leagues → Players table, "Let's just
// build it"): many stats side by side, one row per player in the group, sortable. The pure half:
// which columns, which rows, which order. The group is the Ranking's (the League chip's league
// files, the same chips), so a player's numbers here match their Ranking and Stats tile.

import type { ProtoPlayerMetric } from "@catbento/shared";
import { getPlayerShortName } from "../data/player-utils.js";
import { humanizePositionTemplate } from "../modules/bio-view.js";
import { isLowerBetter, TEMPLATE_AXIS_ORDER, TEMPLATE_ORDER } from "../modules/radar-view.js";
import { ageFromDob } from "../modules/squad-view.js";
import {
  hasTotalMode,
  matchesFilters,
  resolveTotalValue,
  type ActiveFilters,
  type KitPlayer,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";
import type { GroupPlayer } from "./group-players.js";

/** The Template dropdown: the Radar's position templates (their spokes as columns), then the
 *  user's own pick ("Custom", once they've edited the columns). */
export const STATS_TABLE_TEMPLATES: readonly string[] = TEMPLATE_ORDER;
export const CUSTOM_TEMPLATE = "custom";

/** The template a new table opens on: the canvas player's main position, if it has a template,
 *  else Midfielder. Pure. */
export function defaultTemplateFor(position: string | null | undefined): string {
  return position && TEMPLATE_AXIS_ORDER[position] ? position : "midfielder";
}

/** The stat columns: the user's own list once edited, else the template's spokes; only stats the
 *  catalogue knows (a renamed one drops out rather than breaking the table). Pure. */
export function tableColumns(
  template: string,
  custom: readonly string[] | null | undefined,
  catalog: Readonly<Record<string, ProtoPlayerMetric>>,
): string[] {
  const keys = custom && custom.length ? custom : (TEMPLATE_AXIS_ORDER[template] ?? []);
  return [...new Set(keys)].filter((k) => catalog[k] !== undefined);
}

/** One row: who, the fixed columns, then each stat column's value (null = none recorded). */
export interface StatsTableRow {
  /** The group's key (one per player; one per player + position with the Position chip on). */
  key: string;
  playerId: number;
  name: string;
  shortName: string;
  team: string | null;
  /** Their main position ("Centre Back"), or the position this row is about with the chip on. */
  position: string;
  age: number | null;
  minutes: number | null;
  /** Their HOPS rating (0–1; null = none recorded) and its percentile among their gender (null =
   *  no rating, or no distribution to place it on). The table opens sorted by it. */
  hops: number | null;
  hopsPercentile: number | null;
  values: Record<string, number | null>;
}

/** A player's HOPS (rating + percentile), looked up by id; null = none. */
export type HopsLookup = (playerId: number) => { rating: number; percentile: number | null } | null;

/**
 * The group's rows: everyone the chips keep (Ben's own matcher, as the Ranking's), with each
 * column's value per 90 or as a total. Unlike a Ranking, a player with nothing on one column still
 * has a row (their other columns count). Unsorted. Pure.
 */
export function tableRows(
  players: Readonly<Record<string, GroupPlayer>>,
  columns: readonly string[],
  seasonCode: string,
  useTotal: boolean,
  active: ActiveFilters,
  refPlayer: KitPlayer,
  rangeFilters: RangeFilterConfig,
  hopsOf: HopsLookup,
  now: Date = new Date(),
): StatsTableRow[] {
  const rows: StatsTableRow[] = [];
  for (const [key, p] of Object.entries(players)) {
    if (!matchesFilters(p as unknown as KitPlayer, active, refPlayer, rangeFilters)) continue;
    const stats = p.seasons[seasonCode]?.stats ?? p.stats;
    const values: Record<string, number | null> = {};
    for (const k of columns) {
      const metric = { key: k };
      const v = useTotal && hasTotalMode(metric) ? resolveTotalValue(stats, metric) : stats[k];
      values[k] = v === undefined || v === null || Number.isNaN(v) ? null : v;
    }
    rows.push({
      key,
      playerId: p.playerId,
      name: p.name,
      shortName: getPlayerShortName(p),
      team: p.team ?? null,
      position: humanizePositionTemplate(p.positionTemplate),
      age: ageFromDob(p.dob ?? null, now),
      minutes: stats.minutes ?? null,
      hops: hopsOf(p.playerId)?.rating ?? null,
      hopsPercentile: hopsOf(p.playerId)?.percentile ?? null,
      values,
    });
  }
  return rows;
}

/** The fixed columns a heading click can sort by, and each stat column (by its key). */
export type SortColumn = "name" | "team" | "position" | "age" | "minutes" | "hops" | string;
export type SortDir = "asc" | "desc";
export interface TableSort {
  column: SortColumn;
  dir: SortDir;
}

const TEXT_COLUMNS: ReadonlySet<string> = new Set(["name", "team", "position"]);

/** A heading's first click: best first. Names A→Z, age youngest first, minutes and HOPS most
 *  first; a stat highest first, or lowest first where lower is better (fouls, turnovers…). Pure. */
export function firstDir(column: SortColumn): SortDir {
  if (TEXT_COLUMNS.has(column) || column === "age") return "asc";
  if (column === "minutes" || column === "hops") return "desc";
  return isLowerBetter(column) ? "asc" : "desc";
}

/** A heading click: the same heading turns round, another starts best first. Pure. */
export function nextSort(current: TableSort | null, column: SortColumn): TableSort {
  if (current && current.column === column) return { column, dir: current.dir === "asc" ? "desc" : "asc" };
  return { column, dir: firstDir(column) };
}

/** The sort a table opens on: HOPS, highest first (Ben, 2026-11-02: "I think HOPS should be the
 *  initial rank"), so it never opens in no particular order. Pure. */
export const DEFAULT_SORT: TableSort = { column: "hops", dir: "desc" };

function valueOf(r: StatsTableRow, column: SortColumn): string | number | null {
  switch (column) {
    case "name":
      return r.shortName;
    case "team":
      return r.team;
    case "position":
      return r.position;
    case "age":
      return r.age;
    case "minutes":
      return r.minutes;
    case "hops":
      return r.hops;
    default:
      return r.values[column] ?? null;
  }
}

/** Rows in a heading's order. Rows with nothing in that column always go last, either way round;
 *  ties keep A→Z by name. Pure (a new array). */
export function sortRows(rows: readonly StatsTableRow[], sort: TableSort): StatsTableRow[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const va = valueOf(a, sort.column);
    const vb = valueOf(b, sort.column);
    if (va === null || vb === null) {
      if (va !== vb) return va === null ? 1 : -1;
    } else if (va !== vb) {
      const c = typeof va === "string" || typeof vb === "string" ? String(va).localeCompare(String(vb)) : va - vb;
      if (c !== 0) return c * sign;
    }
    return a.shortName.localeCompare(b.shortName);
  });
}
